import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { DwComponent, DwComponentType, DwContext, DwFormControl, DwFormFieldConfig } from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';
import { CourseRepositoryService, CourseSummary } from '../../../courses/services/course-repository.service';

@DwComponent({
  key: 'selected-course-readonly-form-control',
  name: 'Selected Course Readonly Form Control',
  componentType: DwComponentType.formControl,
  isGlobal: true,
})
@Component({
  selector: 'app-selected-course-readonly-form-control',
  templateUrl: './selected-course-readonly-form-control.component.html',
  styleUrls: ['./selected-course-readonly-form-control.component.scss']
})
export class SelectedCourseReadonlyFormControlComponent implements DwFormControl, OnInit, OnDestroy {
  fieldDef: DwFormFieldConfig;
  formGroup: FormGroup;
  inputClass = 'form-control';
  @Input() context: DwContext;

  courseId: number;
  courseSummary: CourseSummary;
  courseName = '';
  courseMetaText = '';
  isLoading = false;
  notFound = false;

  private valueChangesSubscription: Subscription;
  private courseSummarySubscription: Subscription;

  constructor(private courseRepositoryService: CourseRepositoryService) { }

  ngOnInit(): void {
    this.updateCourseSummary(this.control?.value);

    this.valueChangesSubscription = this.control?.valueChanges.subscribe(value => {
      this.updateCourseSummary(value);
    });
  }

  ngOnDestroy(): void {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }

    this.clearCourseSummarySubscription();
  }

  changeCourse(): void {
    if (!this.control || this.control.disabled) {
      return;
    }

    this.control.setValue(null);
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.control.updateValueAndValidity();
  }

  get control(): AbstractControl {
    return this.formGroup?.get(this.fieldDef?.attrName);
  }

  private updateCourseSummary(value: any): void {
    const courseId = this.resolveCourseId(value);

    this.clearCourseSummarySubscription();
    this.courseId = courseId;
    this.courseSummary = null;
    this.courseName = '';
    this.courseMetaText = '';
    this.notFound = false;
    this.isLoading = !!courseId;

    if (!courseId) {
      return;
    }

    this.courseSummarySubscription = this.courseRepositoryService.getCourseSummary(courseId)
      .subscribe(courseSummary => {
        if (this.courseId !== courseId) {
          return;
        }

        this.isLoading = false;
        this.notFound = !courseSummary;
        this.courseSummary = courseSummary;
        this.courseName = courseSummary?.courseName || 'Course unavailable';
        this.courseMetaText = courseSummary
          ? this.buildCourseMetaText(courseSummary)
          : 'Choose another course';
      }, () => {
        if (this.courseId !== courseId) {
          return;
        }

        this.isLoading = false;
        this.notFound = true;
        this.courseName = 'Course unavailable';
        this.courseMetaText = 'Choose another course';
      });
  }

  private resolveCourseId(value: any): number {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const courseId = Number(value);

    return isNaN(courseId) ? null : courseId;
  }

  private buildCourseMetaText(courseSummary: CourseSummary): string {
    const moduleLabel = courseSummary.moduleCount === 1 ? 'module' : 'modules';
    const lessonLabel = courseSummary.lessonCount === 1 ? 'lesson' : 'lessons';

    return `${courseSummary.moduleCount} ${moduleLabel} - ${courseSummary.lessonCount} ${lessonLabel}`;
  }

  private clearCourseSummarySubscription(): void {
    if (this.courseSummarySubscription) {
      this.courseSummarySubscription.unsubscribe();
      this.courseSummarySubscription = null;
    }
  }
}
